/**
 * @fileoverview Utility functions for CSS class management and date formatting
 * @module shared/lib/utils
 */

import { type ClassValue, clsx } from 'clsx'
import { twMerge } from 'tailwind-merge'
import { format } from 'date-fns'
import { pl, enUS } from 'date-fns/locale'

/**
 * Combines class names using clsx and tailwind-merge
 *
 * @param inputs - Class values to combine
 * @returns Combined class string
 *
 * @example
 * ```tsx
 * cn('px-4 py-2', 'bg-blue-500', { 'text-white': isActive })
 * ```
 */
export function cn(...inputs: ClassValue[]): string {
  return twMerge(clsx(inputs))
}

/**
 * Formats a date in human-readable format.
 * For current year: "01 Oct - 31 Oct"
 * For cross-year or previous year: "01 Oct 2024 - 03 Dec 2025"
 *
 * @param date - The date to format
 * @param language - The language code ('en' or 'pl')
 * @returns Formatted date string
 *
 * @example
 * ```tsx
 * // For 2025-10-15 with current year 2025
 * formatDateHuman(new Date('2025-10-15'), 'en') // "15 Oct"
 *
 * // For 2024-10-15 with current year 2025
 * formatDateHuman(new Date('2024-10-15'), 'en') // "15 Oct 2024"
 * ```
 */
export function formatDateHuman(date: Date, language: string): string {
  const locale = language === 'pl' ? pl : enUS
  const currentYear = new Date().getFullYear()
  const dateYear = date.getFullYear()

  if (dateYear === currentYear) {
    return format(date, 'dd MMM', { locale })
  } else {
    return format(date, 'dd MMM yyyy', { locale })
  }
}

/**
 * Formats a date range in human-readable format.
 * For current year: "01 Oct - 31 Oct"
 * For cross-year or previous year: "01 Oct 2024 - 03 Dec 2025"
 *
 * @param fromDate - The start date
 * @param toDate - The end date
 * @param language - The language code ('en' or 'pl')
 * @returns Formatted date range string
 *
 * @example
 * ```tsx
 * // For 2025-10-01 to 2025-10-31 with current year 2025
 * formatDateRangeHuman(new Date('2025-10-01'), new Date('2025-10-31'), 'en') // "01 Oct - 31 Oct"
 *
 * // For 2024-10-01 to 2025-12-03 (cross year)
 * formatDateRangeHuman(new Date('2024-10-01'), new Date('2025-12-03'), 'en') // "01 Oct 2024 - 03 Dec 2025"
 * ```
 */
export function formatDateRangeHuman(fromDate: Date, toDate: Date, language: string): string {
  const fromYear = fromDate.getFullYear()
  const toYear = toDate.getFullYear()
  const currentYear = new Date().getFullYear()

  // Show year for either date if it's not current year or if dates span different years
  const shouldShowYear = fromYear !== currentYear || toYear !== currentYear || fromYear !== toYear

  const locale = language === 'pl' ? pl : enUS

  if (shouldShowYear) {
    return `${format(fromDate, 'dd MMM yyyy', { locale })} - ${format(toDate, 'dd MMM yyyy', { locale })}`
  } else {
    return `${format(fromDate, 'dd MMM', { locale })} - ${format(toDate, 'dd MMM', { locale })}`
  }
}
